import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { LayoutDashboard, Globe, CreditCard, Settings, LogOut, Menu, X, Accessibility } from 'lucide-react';
import { useClerk } from '@clerk/react';

const basePath = import.meta.env.BASE_URL.replace(/\/$/, '');

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Sites', href: '/sites', icon: Globe },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'Settings', href: '/settings', icon: Settings },
];

interface AppShellProps {
  children: React.ReactNode;
}

function Logo() {
  return (
    <Link href="/dashboard" className="flex items-center gap-2 font-semibold group">
      <span aria-hidden="true" className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[image:var(--gradient-hero)] text-primary transition-transform duration-300 group-hover:scale-105 shrink-0 border border-primary/20">
        <Accessibility className="h-5 w-5" />
      </span>
      <span className="font-sans font-bold tracking-tight text-lg text-sidebar-foreground">ADA Widget</span>
    </Link>
  );
}

function NavLinks({ onLinkClick }: { onLinkClick?: () => void }) {
  const [location] = useLocation();
  return (
    <nav className="flex-1 px-3 space-y-1">
      {navigation.map((item) => {
        const isActive =
          location === item.href ||
          (item.href !== '/dashboard' && location.startsWith(item.href));
        const Icon = item.icon;
        return (
          <Link
            key={item.name}
            href={item.href}
            onClick={onLinkClick}
            className={`
              flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200
              ${isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
              }
            `}
            data-testid={`nav-${item.name.toLowerCase()}`}
          >
            <Icon className="w-4 h-4 shrink-0" />
            {item.name}
          </Link>
        );
      })}
    </nav>
  );
}

function SignOutButton({ onSignOut }: { onSignOut?: () => void }) {
  const { signOut } = useClerk();
  return (
    <div className="p-3 border-t border-sidebar-border">
      <button
        onClick={() => {
          onSignOut?.();
          signOut({ redirectUrl: basePath || '/' });
        }}
        className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-all duration-200 w-full"
        data-testid="button-signout"
      >
        <LogOut className="w-4 h-4 shrink-0" />
        Sign Out
      </button>
    </div>
  );
}

export function AppShell({ children }: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-[100dvh] bg-background">
      {/* ── Desktop Sidebar ─────────────────────────────── */}
      <aside className="hidden md:flex w-64 border-r border-sidebar-border bg-sidebar flex-col shrink-0">
        <div className="px-6 py-8">
          <Logo />
        </div>
        <NavLinks />
        <SignOutButton />
      </aside>

      {/* ── Mobile backdrop ──────────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Mobile Drawer ────────────────────────────────── */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 flex flex-col
          bg-sidebar border-r border-sidebar-border
          transform transition-transform duration-300 ease-in-out
          md:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        aria-label="Navigation menu"
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-sidebar-border">
          <Logo />
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground transition-colors"
            aria-label="Close navigation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex flex-col flex-1 py-4 overflow-y-auto">
          <NavLinks onLinkClick={() => setMobileOpen(false)} />
        </div>
        <SignOutButton onSignOut={() => setMobileOpen(false)} />
      </aside>

      {/* ── Main wrapper ─────────────────────────────────── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-4 px-4 py-4 border-b border-border bg-background shrink-0">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 -ml-1 rounded-md text-foreground/70 hover:bg-muted transition-colors"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Logo />
        </header>

        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
